import React from 'react';
import { motion } from 'motion/react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { MessageSquare, Clock, Shield, Headphones, Zap, Users, MessageCircle, Mail } from 'lucide-react';
import { useWhatsAppDemo } from '../../hooks/useWhatsAppDemo';
import { fadeInUp } from '../../lib/motion';

interface Faq {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const handleWhatsApp = useWhatsAppDemo('Ciao, ho alcune domande su ChatLy');

  const faqs: Faq[] = [
    {
      icon: Clock,
      question: "Quanto tempo serve per il setup?",
      answer: "Il setup di ChatLy è incredibilmente veloce! Nella maggior parte dei casi, il tuo bot sarà operativo in meno di 5 minuti. Il nostro team ti guiderà attraverso ogni passaggio: connessione WhatsApp Business API, configurazione messaggi base e test. Per configurazioni più avanzate (integrazioni CRM, automazioni complesse), prevediamo un setup completo entro 24-48 ore."
    },
    {
      icon: MessageSquare,
      question: "È compatibile con WhatsApp Business API?",
      answer: "Sì, ChatLy è completamente compatibile con WhatsApp Business API ufficiale. Utilizziamo solo le API ufficiali di Meta per garantire massima affidabilità e conformità. Il tuo numero WhatsApp Business rimarrà completamente sotto il tuo controllo, e potrai continuare a usare WhatsApp normalmente quando necessario. Supportiamo anche account multipli per gestire più linee di business."
    },
    {
      icon: Users,
      question: "Posso importare i miei contatti esistenti?",
      answer: "Assolutamente! ChatLy supporta l'importazione di contatti da diverse fonti: file CSV/Excel, sistemi CRM (Salesforce, HubSpot, Pipedrive), database MySQL/PostgreSQL, e molti altri. Durante il setup, il nostro team ti aiuterà a migrare tutti i tuoi contatti esistenti mantenendo tutte le informazioni importanti come cronologia acquisti, preferenze e note."
    },
    {
      icon: Headphones,
      question: "Che tipo di supporto offrite?",
      answer: "Ti seguiamo da persone vere, non da un ticket automatico:\n\n• Supporto via email e WhatsApp, con un team che parla italiano\n• Tempi di risposta rapidi, in genere entro poche ore nei giorni lavorativi\n• Onboarding incluso: ti aiutiamo a configurare il bot e a metterlo online\n• Affianchiamo anche dopo il lancio, per ottimizzare risposte e automazioni\n\nIl nostro team è formato specificamente su WhatsApp Business e automazioni conversazionali."
    },
    {
      icon: Zap,
      question: "È possibile fare una prova gratuita?",
      answer: "Sì! Puoi provare ChatLy senza impegno e senza inserire dati di pagamento:\n\n• Demo gratuita personalizzata (circa 30 minuti con il nostro team)\n• Costruiamo insieme una prova sul tuo caso d'uso reale, con le tue domande tipiche\n• Onboarding e configurazione iniziale inclusi, così vedi subito il bot in azione\n• Nessun vincolo: decidi se proseguire solo dopo aver testato i risultati\n\nScrivici su WhatsApp e organizziamo la tua demo gratuita quando preferisci."
    },
    {
      icon: Shield,
      question: "I miei dati sono sicuri? Rispettate il GDPR?",
      answer: "La sicurezza e privacy sono le nostre priorità assolute. ChatLy è completamente conforme al GDPR e alle normative italiane sulla privacy:\n\n• Tutti i dati sono crittografati (SSL/TLS)\n• Server in Europa (conformità GDPR)\n• Backup automatici e sicuri\n• Accesso con autenticazione a due fattori\n• Audit trail completo di tutte le attività\n• Certificazioni ISO 27001 e SOC 2\n\nI tuoi dati rimangono sempre tuoi e puoi esportarli o cancellarli in qualsiasi momento."
    }
  ];

  return (
    <section id="faq" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          {...fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            Domande{' '}
            <span className="text-primary">
              Frequenti
            </span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Tutto quello che devi sapere su ChatLy. Non trovi la risposta che cerchi? Contattaci direttamente!
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <AccordionItem
                    value={`item-${index}`}
                    className="bg-card border border-border rounded-2xl px-6 shadow-sm data-[state=open]:bg-primary/5 transition-colors duration-300"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-6 group">
                      <div className="flex items-center gap-4 w-full">
                        <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-data-[state=open]:bg-primary transition-all duration-300">
                          <faq.icon className="h-5 w-5 text-primary group-data-[state=open]:text-white" aria-hidden="true" />
                        </div>
                        <span className="text-lg font-semibold text-text-primary group-hover:text-primary transition-colors duration-300">
                          {faq.question}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pt-2">
                      <div className="ml-14">
                        <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>

          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-center mt-16"
          >
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl p-8 lg:p-12">
              <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-4">
                Hai altre domande?
              </h3>
              <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
                Il nostro team di esperti è qui per rispondere a tutte le tue domande e aiutarti a scegliere la soluzione migliore per il tuo business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleWhatsApp}
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-primary-dark text-white font-bold py-4 px-8 rounded-full text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  <MessageCircle className="h-5 w-5" aria-hidden="true" />
                  Chatta con noi su WhatsApp
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open('mailto:info@chatly.it?subject=Richiesta informazioni ChatLy', '_blank')}
                  className="inline-flex items-center justify-center gap-2 border-2 border-primary text-primary font-bold py-4 px-8 rounded-full text-lg hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <Mail className="h-5 w-5" aria-hidden="true" />
                  Invia una email
                </motion.button>
              </div>
              
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-text-secondary">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
                  Risposta entro 2 ore
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" aria-hidden="true" />
                  Team di esperti italiani
                </div>
                <div className="flex items-center gap-2">
                  <Headphones className="h-4 w-4 text-primary" aria-hidden="true" />
                  Supporto gratuito
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
